import * as CookieConsent from 'vanilla-cookieconsent';
import { startAutoBlocking } from './autoblock';
import { buildCookieConsentConfig, COOKIE_NAME, offeredCategories, pickLanguage, resolveConfig, withDetected } from './config';
import { buildCookieRows, renderCookieTables, TABLE_ATTRIBUTE, visibleCookieNames, type CookieRows } from './cookie-table';
import { setConsentModeDefaults, updateConsentMode } from './consent-mode';
import { applyIframes } from './iframes';
import { buildLogRecord, sendLogRecord } from './log';
import { bindPreferenceLinks, mountRevisitButton, unmountRevisitButton } from './revisit';
import { SERVICE_PRESETS } from './services';
import { readStoredCategories, toState } from './state';
import { loadLanguage, textsFor } from './translations';
import type { CategoryName, ConsentState, DVConsentApi, DVConsentConfig, ServiceKey } from './types';

export type {
  CategoryName,
  ConsentAction,
  ConsentState,
  DVConsentApi,
  DVConsentConfig,
  OptionalCategory,
  ServiceKey,
  BuiltInLanguage,
} from './types';
export type { ConsentLogRecord } from './log';
export type { LanguageTexts } from './translations';
export { consentModeDefaultsSnippet, toConsentMode } from './consent-mode';
export { SERVICE_PRESETS } from './services';
export { TRACKERS } from './trackers';
export type { Tracker, TrackerId } from './trackers';
export { BUILT_IN_LANGUAGES, EN_TEXTS, loadLanguage } from './translations';
export { DVConsentConfigError } from './config';
export { PACKAGE_VERSION } from './log';

let api: DVConsentApi | null = null;

/**
 * Start the consent banner. Call once, as early as possible (before analytics and ad tags).
 * Throws DVConsentConfigError on invalid config.
 */
export function initConsent(config: DVConsentConfig): DVConsentApi {
  if (api) {
    console.warn('[@datavallis/consent] initConsent() was already called; returning the existing instance.');
    return api;
  }
  const resolved = resolveConfig(config);
  if (resolved.consentMode) setConsentModeDefaults();

  // What the visitor allowed so far (read straight from the cookie, before the engine starts).
  let accepted: string[] = readStoredCategories(COOKIE_NAME, resolved.revision) ?? ['necessary'];
  let effective = resolved;
  let offered = offeredCategories(effective);
  let current: ConsentState | null = null;
  let ready = false;
  let pageWatcher: MutationObserver | null = null;

  // One language per page view: the one the page is in. Its pack loads now, in parallel with the page;
  // blocking below does not wait for it.
  const language = pickLanguage(resolved.languages, resolved.autoDetectLanguage, {
    documentLang: document.documentElement.lang,
    browserLang: typeof navigator === 'undefined' ? '' : navigator.language,
  });
  const textsReady = loadLanguage(language);
  const currentLanguage = (): string => language;

  const rowsFor = (language: string): CookieRows =>
    buildCookieRows({
      language,
      trackers: effective.services,
      consentCookie: { name: COOKIE_NAME, days: resolved.expiresAfterDays },
      siteProvider: resolved.controllerName,
      visibleCookies: visibleCookieNames(),
    });

  const renderTables = (): void => {
    const lang = currentLanguage();
    renderCookieTables(rowsFor(lang), lang);
  };

  // Auto-blocking starts now, synchronously, so it is in place before the parser reaches the site's tags.
  const blocker = resolved.autoBlock
    ? startAutoBlocking({
        isAllowed: (category) => accepted.includes(category),
        shouldBlock: (tracker) => !tracker.neverBlock && !(tracker.google && resolved.googleMode === 'advanced'),
        onDetect: (tracker) => {
          if (!ready) return;
          if (!effective.services.includes(tracker.id)) effective = { ...effective, services: [...effective.services, tracker.id] };
          renderTables();
        },
        onIframeBlocked: () => renderIframes(accepted, currentLanguage()),
      })
    : null;

  const refresh = (cookie: CookieConsent.CookieValue): ConsentState => {
    current = toState(offered, cookie);
    accepted = [...current.accepted];
    blocker?.release(accepted);
    if (resolved.consentMode) updateConsentMode(current.accepted);
    renderIframes(current.accepted, currentLanguage());
    renderTables();
    if (resolved.revisitButton) {
      const button = mountRevisitButton(textsFor(currentLanguage()).revisitLabel, () => CookieConsent.showPreferences());
      button.classList.toggle('kockica-revisit--right', resolved.revisitPosition === 'right');
    }
    return current;
  };

  const record = (state: ConsentState): void => {
    if (resolved.logEndpoint) sendLogRecord(resolved.logEndpoint, buildLogRecord(resolved.siteId, state, window.location));
    config.onChange?.(state);
  };

  // Footer links etc.: <a href="#" data-kockica-preferences>Cookie settings</a>
  bindPreferenceLinks(() => CookieConsent.showPreferences());

  // The banner, script unblocking, iframes and tables wait for the DOM: loaded from <head>, the page's tags and
  // iframes are not parsed yet, and the trackers found by then decide which categories the banner offers.
  onDomReady(() => void textsReady.then(start));

  function start(): void {
    effective = withDetected(resolved, blocker?.detected() ?? []);
    offered = offeredCategories(effective);
    const ccConfig = buildCookieConsentConfig(
      effective,
      {
        onConsent: ({ cookie }) => {
          refresh(cookie);
        },
        onFirstConsent: ({ cookie }) => {
          record(refresh(cookie));
        },
        onChange: ({ cookie }) => {
          record(refresh(cookie));
        },
      },
      rowsFor,
      language,
    );
    void CookieConsent.run(ccConfig).then(() => {
      ready = true;
      pageWatcher = watchPage(() => {
        renderTables();
        renderIframes(accepted, currentLanguage());
      });
      if (!CookieConsent.validConsent()) {
        // Before any choice: keep gated iframes empty and show their placeholders.
        renderIframes(['necessary'], currentLanguage());
        renderTables();
      }
    });
  }

  api = {
    acceptedCategory: (name: CategoryName) => name === 'necessary' || CookieConsent.acceptedCategory(name),
    acceptedService: (service: ServiceKey) => {
      const preset = SERVICE_PRESETS[service];
      return Boolean(preset) && CookieConsent.acceptedService(service, preset.category);
    },
    showPreferences: () => CookieConsent.showPreferences(),
    show: () => CookieConsent.show(true),
    getState: () => current,
    reset: () => {
      CookieConsent.reset(true);
      blocker?.stop();
      pageWatcher?.disconnect();
      unmountRevisitButton();
      current = null;
      api = null;
    },
  };
  return api;
}

const WATCHED = `[${TABLE_ATTRIBUTE}], iframe[data-kockica-category]`;

/**
 * Single-page apps (Next.js, React Router …) swap page content without a reload. Cookie tables and consent-gated
 * iframes that appear later are filled / gated as soon as they are added. Batched per microtask.
 */
function watchPage(update: () => void): MutationObserver {
  let queued = false;
  const observer = new MutationObserver((records) => {
    if (queued) return;
    const relevant = records.some((r) =>
      Array.from(r.addedNodes).some((n) => n instanceof Element && (n.matches(WATCHED) || n.querySelector(WATCHED) !== null)),
    );
    if (!relevant) return;
    queued = true;
    queueMicrotask(() => {
      queued = false;
      update();
    });
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
  return observer;
}

function onDomReady(fn: () => void): void {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn, { once: true });
  else fn();
}

function renderIframes(accepted: readonly string[], language: string): void {
  if (typeof document === 'undefined') return;
  const texts = textsFor(language);
  applyIframes(
    accepted,
    {
      notice: (title) => texts.iframeNotice.replace('{category}', title),
      button: texts.iframeButton,
      categoryTitle: (category) =>
        category in texts.categories ? texts.categories[category as keyof typeof texts.categories].title : category,
    },
    (category) => {
      const keep = CookieConsent.getUserPreferences().acceptedCategories;
      CookieConsent.acceptCategory([...new Set([...keep, category])]);
    },
  );
}
